import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Button, InputError } from '@foodsby/nutrient'
import { Card } from '@foodsby/nutrient-v3'
import {
  clearDailyLinkErrors,
  createDailyLinkStart,
  consumeDailyLinkStart,
  getDailyLinkForDateStart,
} from '../../redux/modules/dailyLink'
import format from 'date-fns/format'
import cn from 'classnames'
import { getQueryParams } from '../../redux/modules/selectors'
import { SpinnerCard } from '../common/Layout'
import { copyToClipboard } from '../../util/copyToClipboard'
import { pushAnalyticsEvent } from '../../util/gtmUtils'

const titles = ['Earn Credits Daily']
const title = titles[Math.floor(Math.random() * titles.length)]
const copyText = `Here's $1 off your lunch today on Foodsby. Follow this link to claim your coupon:`

const _DailyLink = ({
  clearDailyLinkErrors,
  consumeDailyLinkError,
  consumeDailyLinkLoading,
  consumeDailyLinkStart,
  createDailyLinkError,
  createDailyLinkLoading,
  deliveryLocationId,
  getDailyLinkError,
  getDailyLinkForDateStart,
  getDailyLinkLoading,
  history,
  isAuthenticated,
  locationId,
  usersDailyLink,
}) => {
  const [copied, setCopied] = useState(false)
  const [queryParamToken, setQueryParamToken] = useState(undefined)
  const [didConsumeToken, setDidConsumeToken] = useState(false)
  const [shouldHideSelf, setShouldHideSelf] = useState(!isAuthenticated)

  // Hide self if not logged in
  useEffect(() => setShouldHideSelf(!isAuthenticated), [isAuthenticated])

  // Log user for tracking/segmenting purposes
  useEffect(() => {
    if (window.appboy) {
      window.appboy.logCustomEvent('created-daily-link')
    }
    pushAnalyticsEvent('Daily Link', 'Viewed Share Link')
  }, [])

  // Get your daily link for today
  useEffect(() => {
    if (isAuthenticated) {
      getDailyLinkForDateStart(format(new Date(), 'YYYY-MM-DD'))
    }
  }, [isAuthenticated]) //eslint-disable-line

  // Grab token from query param if it exists
  useEffect(() => {
    if (!queryParamToken && history) {
      const { dailyLink } = getQueryParams({ props: { history } })
      setQueryParamToken(dailyLink)
    }
  }, [history, queryParamToken])

  // Consume the daily link if it exists
  useEffect(() => {
    if (!didConsumeToken && queryParamToken) {
      setShouldHideSelf(false)
      consumeDailyLinkStart(queryParamToken)
      setDidConsumeToken(true)
      window.appboy.logCustomEvent('consumed-daily-link')
      pushAnalyticsEvent('Daily Link', 'Followed Share Link')
    }
  }, [consumeDailyLinkStart, didConsumeToken, queryParamToken])

  // On unmount, clear any daily link errors
  useEffect(() => {
    return () => clearDailyLinkErrors()
  }, [clearDailyLinkErrors])

  return (
    <div
      className={cn('WebConV3', {
        hidden: shouldHideSelf,
        consumedDailyLink: didConsumeToken,
      })}
    >
      {!history ||
      (!deliveryLocationId && !locationId) ||
      consumeDailyLinkLoading ||
      getDailyLinkLoading ||
      createDailyLinkLoading ? (
        <SpinnerCard spinnerActive={true} />
      ) : didConsumeToken &&
        !consumeDailyLinkLoading &&
        !(
          createDailyLinkError ||
          getDailyLinkError ||
          consumeDailyLinkError
        ) ? (
        <SuccessModalDailyLink setShouldHideSelf={setShouldHideSelf} />
      ) : (
        isAuthenticated && (
          <Card className="daily-link-container center-xs">
            <h5>{title}</h5>
            <p>
              Share your daily link and earn $1 for each co-worker that orders
              today. <br />
              Your co-workers get $1 off when they order from your link.
            </p>
            <>
              <div
                className={cn('row share-container', {
                  'generated-link': usersDailyLink,
                })}
              >
                <span
                  className={cn('link', {
                    'col-xs-10 link-visible': usersDailyLink,
                    'link-collapsed': !usersDailyLink,
                  })}
                >
                  {usersDailyLink &&
                    `${
                      process.env.REACT_APP_BASE_ORDER_URL
                    }/${deliveryLocationId || locationId}?dailyLink=${
                      usersDailyLink.token
                    }`}
                </span>
                <Button
                  className="col-xs-2"
                  onClick={() => {
                    copyToClipboard(
                      `${copyText} ${
                        process.env.REACT_APP_BASE_ORDER_URL
                      }/${deliveryLocationId || locationId}?dailyLink=${
                        usersDailyLink.token
                      }`,
                    )
                    pushAnalyticsEvent('Daily Link', 'Copied Share Link')
                    setCopied(true)
                  }}
                  style={{ textTransform: 'uppercase' }}
                >
                  Copy
                </Button>
              </div>
              {copied && (
                <p className="copied-text">
                  Copied. Now go share via email, Slack, Teams, text, and more.
                </p>
              )}
              {(createDailyLinkError ||
                getDailyLinkError ||
                consumeDailyLinkError) && (
                <div className="error-container">
                  <InputError>
                    {createDailyLinkError ||
                      getDailyLinkError ||
                      consumeDailyLinkError}
                  </InputError>
                </div>
              )}
            </>
          </Card>
        )
      )}
    </div>
  )
}

export const SuccessModalDailyLink = ({
  setShouldHideSelf,
  successMessage,
}) => (
  <div className="success-notification">
    <p className="notification-message">
      <i className="icon-checkmark success-notification-icon"></i>{' '}
      <strong>{successMessage}</strong>
      <span className="success-notification-text">
        Your $1 off coupon will automagically be applied at checkout!{' '}
        <span aria-label="magic" role="img">
          ✨
        </span>
      </span>
    </p>
    <i
      aria-label="Close notification"
      className="notification-close"
      onClick={() => setShouldHideSelf(true)}
      role="button"
    ></i>
  </div>
)

const mapStateToProps = state => {
  const {
    consumeDailyLinkError,
    consumeDailyLinkLoading,
    createDailyLinkError,
    createDailyLinkLoading,
    getDailyLinkError,
    getDailyLinkLoading,
    usersDailyLink,
  } = state.dailyLink
  const { location } = state.location
  const deliveryLocationId = location && location.deliveryLocationId
  return {
    consumeDailyLinkError,
    consumeDailyLinkLoading,
    createDailyLinkError,
    createDailyLinkLoading,
    deliveryLocationId,
    getDailyLinkError,
    getDailyLinkLoading,
    location,
    usersDailyLink,
  }
}

const mapDispatchToProps = {
  clearDailyLinkErrors,
  createDailyLinkStart,
  consumeDailyLinkStart,
  getDailyLinkForDateStart,
}

export const DailyLink = connect(
  mapStateToProps,
  mapDispatchToProps,
)(_DailyLink)

export default DailyLink
